import { useCallback } from 'react';
import useWatchTime from 'src/hooks/useWatchTime';
import useKeyboardShortcuts from 'src/hooks/useKeyboardShortcuts';
import usePostMessage from 'src/hooks/usePostMessage';
import usePlayback from 'src/hooks/usePlayback';
import { KEYBOARD_SHORTCUTS, POST_MESSAGE_EVENTS } from 'src/interfaces/enums';
import useAutoplay from 'src/hooks/useAutoplay';
import useSubtitle from 'src/hooks/useSubtitle';
import useUI from 'src/hooks/useUI';
import useConfigEvents from '../useConfigEvents';
import useStatsEvents from '../useStatsEvents';
import useSyncStoreEvents from '../useSyncStoreEvents';
import useTrackingEvents from '../useTrackingEvents';
import {
  BACKWARD_SHORTCUT_SECONDS,
  FORWARD_SHORTCUT_SECONDS,
  RAISE_VOLUME_PERCENT,
  REDUCE_VOLUME_PERCENT,
} from './constants';

const usePlayerEvents = () => {
  const { togglePlay, raiseVolumePercent, reduceVolumePercent } = usePlayback();
  const { toggleFullscreen } = useUI();
  const { forwardSeconds, backwardSeconds, setPlayerTime } = useWatchTime();
  const { hideAutoplay } = useAutoplay();
  const { addCue, removeCue, toggleSubtitle } = useSubtitle();
  const { createConfigListeners } = useConfigEvents();
  const { createStatsListeners } = useStatsEvents();
  const { createSyncStoreListeners } = useSyncStoreEvents();
  const { createTrackingListeners } = useTrackingEvents();

  useKeyboardShortcuts({
    [KEYBOARD_SHORTCUTS.TOGGLE_FULLSCREEN]: toggleFullscreen,
    [KEYBOARD_SHORTCUTS.TOGGLE_PLAY]: togglePlay,
    [KEYBOARD_SHORTCUTS.TOGGLE_SUBTITLE]: toggleSubtitle,
    [KEYBOARD_SHORTCUTS.TOGGLE_PLAY_ALTERNATIVE]: togglePlay,
    [KEYBOARD_SHORTCUTS.FORWARD]: () => forwardSeconds(FORWARD_SHORTCUT_SECONDS),
    [KEYBOARD_SHORTCUTS.BACKWARD]: () => backwardSeconds(BACKWARD_SHORTCUT_SECONDS),
    [KEYBOARD_SHORTCUTS.RAISE_VOLUME]: () => raiseVolumePercent(RAISE_VOLUME_PERCENT),
    [KEYBOARD_SHORTCUTS.REDUCE_VOLUME]: () => reduceVolumePercent(REDUCE_VOLUME_PERCENT),
  });

  usePostMessage({
    [POST_MESSAGE_EVENTS.ADD_CUE]: addCue,
    [POST_MESSAGE_EVENTS.REMOVE_CUE]: removeCue,
    [POST_MESSAGE_EVENTS.CHANGEVIDEO]: hideAutoplay,
    [POST_MESSAGE_EVENTS.SET_PLAYER_TIME]: setPlayerTime,
  });

  const createListeners = useCallback(() => {
    createStatsListeners();
    createSyncStoreListeners();
    createTrackingListeners();
    createConfigListeners();
  }, []);

  return { createListeners };
};

export default usePlayerEvents;
